import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Minus, Plus, Check, ShieldAlert, ChevronLeft, Box } from "lucide-react";
import { PRODUCTS, PRODUCT_LIST, SUBSCRIBE_INTERVALS, SUBSCRIBE_DISCOUNT } from "@/data/catalog";
import { formatPrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { Product3DViewer } from "@/components/Product3DViewer";

export default function ProductPage() {
  const { handle } = useParams();
  const product = PRODUCT_LIST.find((p) => p.handle === handle);
  const { addItem, setOpen } = useCart();

  const [qty, setQty] = useState(1);
  const [plan, setPlan] = useState("onetime");
  const [interval, setInterval] = useState("4w");
  const [media, setMedia] = useState(0);

  if (!product) return <Navigate to="/" replace />;

  const gallery = product.gallery || [{ type: "img", src: product.image }];
  const current = gallery[Math.min(media, gallery.length - 1)];

  const subscribe = plan === "subscribe";
  const unit = subscribe ? product.price * (1 - SUBSCRIBE_DISCOUNT) : product.price;

  const handleAdd = () => {
    addItem(product, { qty, sellingPlan: plan, interval });
  };

  const related = PRODUCT_LIST.filter((p) => p.handle !== handle);

  return (
    <main className="bg-[#F8F5EF] pt-24 md:pt-28" data-testid="product-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#6F503A]/70 hover:text-[#6F503A] mb-8">
          <ChevronLeft className="w-4 h-4" /> Back to shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Media gallery */}
          <div>
            <div className="bg-white rounded-3xl p-4 md:p-8 shadow-[0_4px_24px_rgba(111,80,58,0.05)] flex items-center justify-center min-h-[380px] md:min-h-[460px]">
              {current.type === "3d" ? (
                <Product3DViewer
                  src={current.src}
                  fallbackImage={product.image}
                  heightClass="h-[360px] md:h-[440px]"
                  testId="pp-3d"
                />
              ) : (
                <img
                  src={current.src}
                  alt={product.name}
                  className="w-full max-w-md object-contain"
                  data-testid="product-image"
                />
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mt-4">
                {gallery.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setMedia(i)}
                    data-testid={`pp-thumb-${i}`}
                    className={`relative w-20 h-20 rounded-2xl overflow-hidden bg-white border-2 transition-colors ${
                      media === i ? "border-[#6F503A]" : "border-transparent hover:border-[#6F503A]/30"
                    }`}
                  >
                    <img
                      src={m.type === "3d" ? product.image : m.src}
                      alt=""
                      className="w-full h-full object-contain p-2"
                    />
                    {m.type === "3d" && (
                      <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#6F503A] text-[#F8F5EF] text-[9px] font-semibold">
                        <Box className="w-2.5 h-2.5" /> 3D
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {current.type === "3d" && (
              <p className="text-center text-xs text-[#6F503A]/40 mt-2">Drag to spin · auto-rotating 3D view</p>
            )}
          </div>

          {/* Info */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B8C4A3]">{product.tagline}</span>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl font-semibold text-[#6F503A]">{product.name}</h1>
            <p className="mt-2 text-[#6F503A]/60">{product.subtitle}</p>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-heading text-3xl font-semibold text-[#6F503A]" data-testid="product-price">
                {formatPrice(unit)}
              </span>
              {subscribe && <span className="text-[#6F503A]/40 line-through">{formatPrice(product.price)}</span>}
              {product.compareAt && !subscribe && (
                <span className="text-[#6F503A]/40 line-through">{formatPrice(product.compareAt)}</span>
              )}
            </div>

            <p className="mt-5 text-[#6F503A]/80 leading-relaxed">{product.description}</p>

            {/* Benefits */}
            <ul className="mt-6 space-y-2.5">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#6F503A]/80">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#B8C4A3]/25 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-[#6F503A]" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            {/* Purchase options */}
            <div className="mt-7 space-y-3">
              <PlanOption
                active={plan === "onetime"}
                onClick={() => setPlan("onetime")}
                title="One-time purchase"
                price={formatPrice(product.price)}
                testid="plan-onetime"
              />
              <PlanOption
                active={plan === "subscribe"}
                onClick={() => setPlan("subscribe")}
                title="Subscribe & Save 15%"
                price={formatPrice(product.price * (1 - SUBSCRIBE_DISCOUNT))}
                testid="plan-subscribe"
              >
                {subscribe && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SUBSCRIBE_INTERVALS.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setInterval(opt.id);
                        }}
                        data-testid={`pp-interval-${opt.id}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          interval === opt.id
                            ? "bg-[#6F503A] text-[#F8F5EF] border-[#6F503A]"
                            : "border-[#6F503A]/20 text-[#6F503A] hover:bg-[#E9E2D6]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </PlanOption>
            </div>

            {/* Qty + add */}
            <div className="mt-7 flex items-center gap-4">
              <div className="flex items-center border border-[#6F503A]/20 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pp-qty-dec" className="p-3 text-[#6F503A]">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium text-[#6F503A]" data-testid="pp-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="pp-qty-inc" className="p-3 text-[#6F503A]">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAdd}
                data-testid="pp-add-to-cart"
                className="flex-1 py-4 rounded-full bg-[#6F503A] text-[#F8F5EF] font-medium hover:bg-[#5b4130] transition-colors"
              >
                Add to Cart · {formatPrice(unit * qty)}
              </button>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <DetailCard title="Ingredients">{product.ingredients}</DetailCard>
          <DetailCard title="How to use">
            <ol className="space-y-2 list-decimal list-inside">
              {product.howToUse.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </DetailCard>
          <DetailCard title="Warnings" icon={<ShieldAlert className="w-4 h-4 text-[#6F503A]" />}>
            {product.warnings}
          </DetailCard>
        </div>

        {/* Related */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl font-semibold text-[#6F503A] mb-6">You may also like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

const PlanOption = ({ active, onClick, title, price, children, testid }) => (
  <div
    onClick={onClick}
    data-testid={testid}
    className={`cursor-pointer rounded-2xl border-2 p-4 transition-colors ${
      active ? "border-[#6F503A] bg-white" : "border-[#6F503A]/15 hover:border-[#6F503A]/30"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-3 font-medium text-[#6F503A]">
        <span className={`w-4 h-4 rounded-full border-2 ${active ? "border-[#6F503A] bg-[#6F503A]" : "border-[#6F503A]/30"}`} />
        {title}
      </span>
      <span className="font-semibold text-[#6F503A]">{price}</span>
    </div>
    {children}
  </div>
);

const DetailCard = ({ title, children, icon }) => (
  <div className="bg-white rounded-3xl p-7 shadow-[0_4px_24px_rgba(111,80,58,0.05)]">
    <h3 className="font-heading font-semibold text-[#6F503A] mb-3 flex items-center gap-2">
      {icon} {title}
    </h3>
    <div className="text-sm text-[#6F503A]/70 leading-relaxed">{children}</div>
  </div>
);
